import React, { useEffect, useMemo, useRef, useState } from 'react';
import { STUDENT_CODING_LANGUAGES } from '../../constants/codingLanguages';
import SlideToConfirm from '../common/SlideToConfirm';
import '../common/userDropdown.css';
import './AttendRoundModal.css';

export type AttendRoundType = 'aptitude' | 'coding' | 'gd' | string;

export interface AttendRoundModalProps {
  roundNum: number;
  roundType: AttendRoundType;
  loading: boolean;
  error: string;
  loadingNotice: string;
  showLanguagePicker?: boolean;
  selectedLanguage?: string;
  onLanguageChange?: (language: string) => void;
  onClose: () => void;
  onStart: (language?: string) => void;
}

function roundTypeLabel(type: AttendRoundType): string {
  if (type === 'aptitude') return 'Mock interview';
  if (type === 'coding') return 'Coding challenge';
  if (type === 'gd') return 'Group discussion';
  return 'Start round';
}

function roundTypeSuffix(type: AttendRoundType): string {
  if (type === 'aptitude') return ' (mock interview)';
  if (type === 'coding') return ' (coding challenge)';
  if (type === 'gd') return ' (group discussion)';
  return '';
}

const AttendRoundModal: React.FC<AttendRoundModalProps> = ({
  roundNum,
  roundType,
  loading,
  error,
  loadingNotice,
  showLanguagePicker = false,
  selectedLanguage = '',
  onLanguageChange,
  onClose,
  onStart,
}) => {
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement | null>(null);

  const selectedLanguageLabel = useMemo(
    () =>
      STUDENT_CODING_LANGUAGES.find((option) => option.value === selectedLanguage)?.label ??
      'Select language',
    [selectedLanguage]
  );

  useEffect(() => {
    if (!isLanguageMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!languageMenuRef.current?.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isLanguageMenuOpen]);

  useEffect(() => {
    if (loading) {
      setIsLanguageMenuOpen(false);
    }
  }, [loading]);

  const handleBackdropClick = () => {
    if (loading) return;
    onClose();
  };

  const canStart = !showLanguagePicker || Boolean(selectedLanguage);

  const handleConfirm = () => {
    if (!canStart) return;
    onStart(showLanguagePicker ? selectedLanguage : undefined);
  };

  return (
    <div
      className="attend-round-modal-backdrop"
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div
        className="attend-round-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="attend-round-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="attend-round-modal__header">
          <h2 id="attend-round-modal-title" className="attend-round-modal__title">
            {roundTypeLabel(roundType)}
          </h2>
          <button
            type="button"
            className="attend-round-modal__close"
            disabled={loading}
            aria-label="Close"
            onClick={() => {
              if (loading) return;
              onClose();
            }}
          >
            ✕
          </button>
        </header>

        <div className="attend-round-modal__body">
          {loading ? (
            <>
              <p className="attend-round-modal__notice" role="status" aria-live="polite">
                {loadingNotice}
              </p>
              <div className="attend-round-modal__loading">
                <span className="attend-round-modal__spinner" aria-hidden="true" />
                <span className="attend-round-modal__loading-label">Starting your session…</span>
              </div>
            </>
          ) : (
            <>
              <p className="attend-round-modal__desc">
                You are about to start <strong>Round {roundNum}</strong>
                {roundTypeSuffix(roundType)}.
                {showLanguagePicker
                  ? ' Choose your programming language, then slide to confirm.'
                  : ' Slide to confirm and your session will load.'}
              </p>
              {showLanguagePicker && (
                <div className="attend-round-modal__field">
                  <span className="attend-round-modal__label" id="attend-round-language-label">
                    Choose programming language
                  </span>
                  <div className="attend-round-modal__language-wrap" ref={languageMenuRef}>
                    <button
                      type="button"
                      id="attend-round-language"
                      className={`attend-round-modal__language-trigger${isLanguageMenuOpen ? ' open' : ''}`}
                      onClick={() => setIsLanguageMenuOpen((open) => !open)}
                      aria-haspopup="menu"
                      aria-expanded={isLanguageMenuOpen}
                      aria-labelledby="attend-round-language-label attend-round-language"
                    >
                      {selectedLanguageLabel}
                    </button>

                    {isLanguageMenuOpen && (
                      <div
                        className="user-dropdown-menu attend-round-modal__language-menu"
                        role="menu"
                        aria-labelledby="attend-round-language-label"
                      >
                        {STUDENT_CODING_LANGUAGES.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`user-dropdown-item${selectedLanguage === option.value ? ' active' : ''}`}
                            role="menuitem"
                            onClick={() => {
                              onLanguageChange?.(option.value);
                              setIsLanguageMenuOpen(false);
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <SlideToConfirm
                label="Slide to start round"
                confirmedLabel="Confirmed!"
                onConfirm={handleConfirm}
                disabled={loading || !canStart}
                resetKey={error}
              />
            </>
          )}
          {error && <p className="attend-round-modal__error">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default AttendRoundModal;
